import { useEffect } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical"
import { toast } from "sonner"

import {
  ALLOWED_IMAGE_TYPES,
  INSERT_INLINE_IMAGE_COMMAND,
  MAX_IMAGE_SIZE_BYTES,
} from "@/components/lexical/plugins/inline-image"
import { getClipboardImageFiles } from "@/components/lexical/plugins/paste-image/clipboard"

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result)
      } else {
        reject(new Error("Failed to read pasted image"))
      }
    }

    reader.onerror = () => reject(new Error("Failed to read pasted image"))
    reader.readAsDataURL(file)
  })
}

export default function PasteImagePlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!(event instanceof ClipboardEvent)) return false

        const pastedImageFiles = getClipboardImageFiles(event.clipboardData)
        if (!pastedImageFiles.length) return false

        event.preventDefault()

        const validFiles = pastedImageFiles.filter((file) => {
          if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
            toast.error("Pasted image type is not supported")
            return false
          }

          if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error("Pasted image must be 10 MB or smaller")
            return false
          }

          return true
        })

        if (!validFiles.length) {
          return true
        }

        void (async () => {
          for (const file of validFiles) {
            try {
              const src = await readFileAsDataUrl(file)
              editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, {
                altText: file.name || "Pasted image",
                position: "full",
                showCaption: false,
                src,
              })
            } catch {
              toast.error("Failed to paste image")
            }
          }
        })()

        return true
      },
      COMMAND_PRIORITY_HIGH
    )
  }, [editor])

  return null
}
